import { Select } from "antd";
import type { MessageInstance } from "antd/es/message/interface";
import type { MutableRefObject } from "react";
import { type KeyboardEventHandler, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { api } from "@/api/api.v2";
import type { RootState } from "@/components/WellLoggingOperations/types";
import { Button } from "@/shared/MiButton/MiButton";
import Modal from "@/shared/Modal";
import type { VersionInfo } from "@/store/geo-test/types";
import {
	getObjectWithMinPriorityAndLatestVersionDate,
	useActiveGroupWellIds,
} from "../../utils";
import { HorizonContacts } from "./HorizonContacts";

type InitGeoProfilesModalProps = {
	visible: boolean;
	onClose: () => void;
	onCancel: () => void;
	messageApi: MessageInstance;
	containerRef: MutableRefObject<HTMLDivElement>;
};

export const InitGeoProfilesModal = ({
	visible,
	onClose,
	onCancel,
	messageApi,
	containerRef,
}: InitGeoProfilesModalProps) => {
	const dispatch = useDispatch();

	const { versionId, selectedHorizon } = useSelector(
		(store: RootState) => store.geoTest,
	);
	const lists = useSelector((state: RootState) => state.lists);
	const {
		loadGeoData,
		setVersionId,
		setSelectedHorizon,
		setWellIds,
		setIsRigis,
	} = dispatch.geoTest;

	const currentIds = useActiveGroupWellIds();

	const versionMap = useRef<Record<number, boolean>>({});

	const [isVersionsLoading, setIsVersionLoading] = useState<boolean>(false);
	const [versionList, setVersionList] = useState<VersionInfo[]>([]);
	const [localVersion, setLocalVersion] = useState(versionId);
	const [localHorizon, setLocalHorizon] = useState(selectedHorizon);

	const [isMounted, setIsMounted] = useState(false);

	useEffect(() => {
		setIsMounted(true);
		if ((lists?.activeGroupKey || lists.activeItemKey) && lists.data) {
			const activeGroup = (lists.data ?? []).filter(
				(item) =>
					item.key === lists.activeGroupKey || item.key === lists.activeItemKey,
			)[0];
			if (activeGroup && activeGroup.node === "group") {
				const groupIds = activeGroup.children.map((child) => child.id);
				if (groupIds && groupIds.length > 0) {
					setVersionList([]);
					setIsVersionLoading(true);
					void api.carottage
						.fetchFgiList({ wellsId: groupIds })
						.then((fgiList) => {
							if (fgiList?.length > 0) {
								const fgiWithTopPriority =
									getObjectWithMinPriorityAndLatestVersionDate(fgiList);
								setLocalVersion(fgiWithTopPriority.stratumVersionId);
								const versions = fgiList.map(({ source, stratumVersionId }) => {
									versionMap.current[stratumVersionId] = false;
									return {
										label: (
											<span className="text-xs" title={source}>
												{source}
											</span>
										),
										value: stratumVersionId,
									};
								});
								setVersionList((prev) => [...prev, ...versions]);
							}
						});

					void api.carottage
						.fetchRigisList({ wellsId: groupIds })
						.then((rigisList) => {
							if (rigisList?.length > 0) {
								if (!localVersion) {
									setLocalVersion(rigisList[0].stratumVersionId);
								}
								const versions = rigisList.map(
									({ description, stratumVersionId }) => {
										versionMap.current[stratumVersionId] = false;
										return {
											label: (
												<span className="text-xs" title={description}>
													{description}
												</span>
											),
											value: stratumVersionId,
										};
									},
								);
								setVersionList((prev) => [...prev, ...versions]);
							}
						});
				}
				setIsVersionLoading(false);
			}
		}
	}, [lists?.activeGroupKey, lists.activeItemKey]);

	if (!isMounted) return null;

	const handleCancel = async () => {
		// if (!geoData) {
		//     await router.push(ROUTES.HOME, ROUTES.HOME, { shallow: true });
		// }
		onCancel();
	};

	const handleClose = () => {
		setVersionId(localVersion);
		setSelectedHorizon(localHorizon);
		setIsRigis(localVersion ? versionMap.current[localVersion] : false);
		setWellIds(currentIds);

		loadGeoData({ messageApi });

		onClose();
	};

	const handleKeyDown: KeyboardEventHandler<HTMLElement> = (e) => {
		if (e.key === "Enter") {
			onClose;
		}
	};

	if ((lists?.activeGroupKey || lists.activeItemKey) && lists.data) {
		const activeGroup = (lists.data ?? []).filter(
			(item) =>
				item.key === lists.activeGroupKey || item.key === lists.activeItemKey,
		)[0];
		if (activeGroup && activeGroup.node === "group") {
			return (
				<Modal
					title={"Параметры для отображения геологического профиля"}
					open={visible}
					onCancel={handleCancel}
					width={400}
					footer={null}
					getContainer={() => containerRef.current}
				>
					<div className={`h-full  w-full`}>
						<div className="flex h-full flex-col !p-2 text-xs font-semibold">
							<div>Список параметров:</div>
							<div className="border rounded-s dark:border-fill-primary mt-2">
								<HorizonContacts
									selectedHorizon={localHorizon}
									setSelectedHorizon={setLocalHorizon}
								/>
								<Select
									loading={isVersionsLoading}
									className="!m-4"
									options={versionList}
									onChange={setLocalVersion}
									value={localVersion}
									style={{ width: "calc(100% - 2rem)" }}
								/>
							</div>
							<div className="flex items-center justify-end mt-2">
								<Button className="!mr-2" onClick={onCancel}>
									Отменить
								</Button>
								<Button
									onKeyDown={handleKeyDown}
									type="primary"
									onClick={handleClose}
									disabled={
										localHorizon?.[0]?.depth != null &&
										localHorizon?.[1]?.depth != null &&
										localHorizon[0].depth >= localHorizon[1].depth &&
										localHorizon[0].depth > 0 &&
										localHorizon[1].depth > 0 &&
										!localHorizon[0].checked
									}
								>
									Загрузить
								</Button>
							</div>
						</div>
					</div>
				</Modal>
			);
		} else {
			return null;
		}
	} else {
		return null;
	}
};
